import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const LOCALE_TAG = 'en-US';

/** 12 abbreviated month labels consistent with CalendarHeader locale. */
const MONTH_LABELS_SHORT = Array.from({ length: 12 }, (_, i) =>
  new Date(2020, i, 1).toLocaleDateString(LOCALE_TAG, { month: 'short' }),
);

/** Month/year picker opened from Chevron beside the calendar title — grid + year steppers. */
export default function MonthYearPickerDropdown({
  currentMonth,
  minYear,
  maxYear,
  onSelectMonth,
}) {
  const [open, setOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(() => currentMonth.getFullYear());
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (open) setPickerYear(currentMonth.getFullYear());
  }, [open, currentMonth]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e) => {
      const root = triggerRef.current;
      const panel = panelRef.current;
      const target = e.target;
      if (root?.contains(target) || panel?.contains(target)) return;
      setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const applyYearDelta = useCallback(
    (dy) => {
      setPickerYear((y) => Math.min(maxYear, Math.max(minYear, y + dy)));
    },
    [minYear, maxYear],
  );

  const yearLabel = String(pickerYear);
  const currY = currentMonth.getFullYear();
  const currM = currentMonth.getMonth();

  const canPrevYear = pickerYear > minYear;
  const canNextYear = pickerYear < maxYear;

  const headline = useMemo(() => {
    const longFull = currentMonth.toLocaleDateString(LOCALE_TAG, {
      month: 'long',
      year: 'numeric',
    });
    const shortFull = currentMonth.toLocaleDateString(LOCALE_TAG, {
      month: 'short',
      year: 'numeric',
    });
    return { longFull, shortFull };
  }, [currentMonth]);

  const toggle = () => setOpen((v) => !v);

  return (
    <div className="relative flex min-w-0 max-w-full items-center">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Choose month and year, ${currentMonth.toLocaleDateString(LOCALE_TAG, {
          month: 'long',
          year: 'numeric',
        })}`}
        className={`group flex min-w-0 max-w-full items-center gap-1 rounded-lg px-1.5 py-0.5 text-left transition-colors hover:bg-gray-100 ${
          open ? 'bg-gray-100' : ''
        }`}
      >
        <span className="min-w-0 truncate text-base font-semibold text-gray-800 group-hover:text-gray-900 sm:text-lg">
          <span className="hidden sm:inline">{headline.longFull}</span>
          <span className="inline sm:hidden">{headline.shortFull}</span>
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform group-hover:text-gray-700 ${
            open ? 'rotate-180 text-gray-700' : ''
          }`}
          aria-hidden
        />
      </button>

      {open ? (
        <div
          ref={panelRef}
          id="calendar-month-year-picker-panel"
          role="dialog"
          aria-label={`Select month — ${pickerYear}`}
          className="absolute left-0 top-full z-[60] mt-1 min-w-[16.5rem] rounded-xl border border-gray-200 bg-white p-3 shadow-lg ring-1 ring-black/5 sm:min-w-[18rem]"
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <button
              type="button"
              disabled={!canPrevYear}
              onClick={() => applyYearDelta(-1)}
              className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-30"
              aria-label="Previous year"
            >
              <ChevronLeftIcon className="h-5 w-5" aria-hidden />
            </button>
            <span className="min-w-0 flex-1 text-center text-sm font-semibold tabular-nums text-gray-800">
              {yearLabel}
            </span>
            <button
              type="button"
              disabled={!canNextYear}
              onClick={() => applyYearDelta(1)}
              className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-30"
              aria-label="Next year"
            >
              <ChevronRightIcon className="h-5 w-5" aria-hidden />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {MONTH_LABELS_SHORT.map((label, idx) => {
              const isSelected = pickerYear === currY && idx === currM;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    onSelectMonth(pickerYear, idx);
                    setOpen(false);
                  }}
                  className={`rounded-lg px-1.5 py-2 text-center text-xs font-medium transition-colors sm:text-sm ${
                    isSelected
                      ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'
                      : 'bg-gray-50 text-gray-800 hover:bg-gray-100'
                  }`}
                  aria-current={isSelected ? 'true' : undefined}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
